import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Megaphone, Send, X, Info, AlertTriangle, CheckCircle2, RotateCcw, Calendar, Clock, Eye, EyeOff, Users, Bold as BoldIcon, Link as LinkIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { renderRichAnnouncement } from "../lib/announcementFormat";

const TEAL = "#0D9488";
const TONE_META = {
  info:    { label: "Info",      icon: Info,          chipCls: "bg-teal-50 text-teal-700 border-teal-200" },
  warning: { label: "Warning",   icon: AlertTriangle, chipCls: "bg-amber-50 text-amber-700 border-amber-200" },
  success: { label: "Good news", icon: CheckCircle2,  chipCls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

// Convert local datetime-local string -> ISO UTC (or "" for clear).
function localToUtcIso(local) {
  if (!local) return "";
  const dt = new Date(local);
  if (isNaN(dt.getTime())) return "";
  return dt.toISOString();
}
function minLocalDateTime() {
  const now = new Date(Date.now() + 60_000); // +1 min so past-dating fails softly
  const off = now.getTimezoneOffset();
  const local = new Date(now.getTime() - off * 60_000);
  return local.toISOString().slice(0, 16);
}

export default function AdminAnnouncementsCard() {
  const [body, setBody] = useState("");
  const [tone, setTone] = useState("info");
  const [scheduleFor, setScheduleFor] = useState(""); // datetime-local
  const [showFormatting, setShowFormatting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = () => {
    setLoadingHistory(true);
    api.get("/admin/announcements")
      .then(({ data }) => setHistory(Array.isArray(data) ? data : []))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  };
  useEffect(() => { loadHistory(); }, []);

  const active = history.find((h) => h.is_active) || null;
  const scheduled = history.filter((h) => h.status === "scheduled");

  const preview = useMemo(() => renderRichAnnouncement(body), [body]);

  const publish = async (e) => {
    e?.preventDefault?.();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const payload = { body: body.trim(), tone };
      const iso = localToUtcIso(scheduleFor);
      if (iso) payload.scheduled_for = iso;
      const { data } = await api.post("/admin/announcements", payload);
      if (data.status === "scheduled") {
        toast.success(`Scheduled — goes live ${new Date(data.scheduled_for).toLocaleString()}`);
      } else {
        toast.success(active ? "Published — previous announcement retracted" : "Published to all users");
      }
      setBody(""); setScheduleFor("");
      window.dispatchEvent(new CustomEvent("amino:announcement-changed"));
      loadHistory();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const retract = async (id, wasScheduled) => {
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success(wasScheduled ? "Scheduled announcement canceled" : "Announcement retracted");
      window.dispatchEvent(new CustomEvent("amino:announcement-changed"));
      loadHistory();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const insertMarkup = (before, after = "") => {
    const el = document.querySelector('[data-testid="admin-announcements-body"]');
    if (!el) { setBody((b) => b + before + after); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const selected = body.slice(start, end);
    const next = body.slice(0, start) + before + selected + after + body.slice(end);
    setBody(next);
    // restore focus + selection just after insertion
    setTimeout(() => {
      el.focus();
      const pos = start + before.length + selected.length + after.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const ActiveIcon = active ? (TONE_META[active.tone] || TONE_META.info).icon : null;
  const scheduleErr = scheduleFor && new Date(scheduleFor) <= new Date() ? "Time is in the past — announcement will publish immediately." : "";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="admin-announcements-card">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Announcements</div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Publish to all users</h3>
      <p className="text-sm text-slate-500 mb-4">
        Publish now or queue for later — only one banner is live site-wide at a time. Users can dismiss once they've read it, and you'll see the seen vs dismissed reach below.
      </p>

      {active && (
        <div className={`border rounded-lg p-3 mb-4 flex items-start gap-3 ${TONE_META[active.tone]?.chipCls || TONE_META.info.chipCls}`}
             data-testid="admin-announcements-current">
          {ActiveIcon && <ActiveIcon className="w-4 h-4 mt-0.5 shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold">Currently live · {(active.published_at || "").slice(0, 16).replace("T", " · ")}</div>
            <div className="text-sm mt-1"
                 dangerouslySetInnerHTML={{ __html: renderRichAnnouncement(active.body) }} />
            <div className="text-[11px] mt-2 flex items-center gap-3 font-mono-num opacity-80">
              <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {active.seen_count} seen</span>
              <span className="opacity-50">·</span>
              <span className="inline-flex items-center gap-1"><EyeOff className="w-3 h-3" /> {active.dismissed_count} dismissed</span>
            </div>
          </div>
          <Button type="button" size="sm" variant="ghost"
                  onClick={() => retract(active.id, false)}
                  className="text-slate-700 hover:bg-white/60 shrink-0"
                  data-testid="admin-announcements-retract">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Retract
          </Button>
        </div>
      )}

      {scheduled.length > 0 && (
        <div className="border border-teal-100 bg-teal-50/40 rounded-lg p-3 mb-4" data-testid="admin-announcements-scheduled-list">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-teal-800 mb-2 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Queued ({scheduled.length})
          </div>
          <ul className="space-y-1.5">
            {scheduled.map((s) => (
              <li key={s.id} className="flex items-start gap-2 text-[13px]" data-testid={`admin-announcements-scheduled-${s.id}`}>
                <Clock className="w-3.5 h-3.5 mt-0.5 text-teal-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-800 truncate">{s.body}</div>
                  <div className="text-[10px] text-slate-500 font-mono-num">
                    Goes live {s.scheduled_for ? new Date(s.scheduled_for).toLocaleString() : "?"}
                  </div>
                </div>
                <Button type="button" size="sm" variant="ghost"
                        onClick={() => retract(s.id, true)}
                        className="h-6 px-2 text-rose-600 hover:bg-rose-50 shrink-0"
                        data-testid={`admin-announcements-cancel-${s.id}`}>
                  Cancel
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={publish} data-testid="admin-announcements-form">
        <div className="flex items-center justify-between mb-1">
          <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Message</label>
          <button type="button" onClick={() => setShowFormatting((s) => !s)}
                  className="text-[10px] uppercase tracking-widest font-semibold text-teal-700 hover:text-teal-900"
                  data-testid="admin-announcements-format-toggle">
            {showFormatting ? "Hide" : "Formatting help"}
          </button>
        </div>

        {showFormatting && (
          <div className="border border-slate-200 rounded-md p-2.5 mb-2 bg-slate-50/60 text-[11px] text-slate-600 space-y-1"
               data-testid="admin-announcements-format-help">
            <div>Use <code className="bg-white px-1 rounded border border-slate-200 font-mono-num">**bold**</code> for emphasis · <code className="bg-white px-1 rounded border border-slate-200 font-mono-num">[link text](https://…)</code> to link to a status page.</div>
            <div className="flex items-center gap-2 pt-1">
              <Button type="button" size="sm" variant="outline" onClick={() => insertMarkup("**", "**")}
                      className="h-7 px-2 text-[11px]" data-testid="admin-announcements-insert-bold">
                <BoldIcon className="w-3 h-3 mr-1" /> Bold
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => insertMarkup("[", "](https://)")}
                      className="h-7 px-2 text-[11px]" data-testid="admin-announcements-insert-link">
                <LinkIcon className="w-3 h-3 mr-1" /> Link
              </Button>
            </div>
          </div>
        )}

        <Textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, 1000))}
          placeholder={"e.g. **Maintenance** tonight at 10 PM — see [status page](https://status.example.com)."}
          className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] text-sm"
          data-testid="admin-announcements-body"
        />
        <div className="flex items-center justify-between mt-1">
          <div className="text-[10px] text-slate-400 font-mono-num">{body.length}/1000</div>
          {body.trim() && (
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">Preview ↓</div>
          )}
        </div>

        {body.trim() && (
          <div className={`border rounded-md p-2.5 mt-2 text-sm ${TONE_META[tone]?.chipCls || TONE_META.info.chipCls}`}
               data-testid="admin-announcements-preview"
               dangerouslySetInnerHTML={{ __html: preview }} />
        )}

        <div className="flex items-center flex-wrap gap-3 mt-3">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tone</span>
          {Object.entries(TONE_META).map(([k, meta]) => {
            const Icon = meta.icon;
            const isActive = tone === k;
            return (
              <button
                key={k} type="button"
                onClick={() => setTone(k)}
                className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-full border text-[11px] font-semibold transition-colors ${
                  isActive ? meta.chipCls : "border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                }`}
                data-testid={`admin-announcements-tone-${k}`}
              >
                <Icon className="w-3 h-3" /> {meta.label}
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Schedule for (optional)
            </label>
            <Input
              type="datetime-local"
              value={scheduleFor}
              min={minLocalDateTime()}
              onChange={(e) => setScheduleFor(e.target.value)}
              className="bg-white border-slate-200 focus-visible:ring-0 focus-visible:border-[#0D9488] h-9 text-sm"
              data-testid="admin-announcements-schedule"
            />
            {scheduleErr && (
              <div className="text-[10px] text-amber-700 mt-1">{scheduleErr}</div>
            )}
          </div>
          <Button type="submit"
                  disabled={!body.trim() || busy}
                  className="text-white h-9"
                  style={{ background: TEAL }}
                  data-testid="admin-announcements-publish">
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {busy
              ? "Publishing…"
              : scheduleFor && new Date(scheduleFor) > new Date()
                ? "Schedule"
                : active
                  ? "Replace with new"
                  : "Publish to all users"}
          </Button>
        </div>
      </form>

      {/* History */}
      <div className="mt-6 pt-4 border-t border-slate-100">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Recent history</div>
        {loadingHistory ? (
          <div className="h-20 bg-slate-50 rounded animate-pulse" />
        ) : history.length === 0 ? (
          <div className="text-xs text-slate-500 italic">Nothing published yet.</div>
        ) : (
          <ul className="space-y-1.5 max-h-72 overflow-auto" data-testid="admin-announcements-history">
            {history.map((h) => {
              const isScheduled = h.status === "scheduled";
              const isLive = h.is_active;
              const total = h.seen_count || 0;
              const dRatio = total > 0 ? Math.round((h.dismissed_count / total) * 100) : 0;
              return (
                <li key={h.id}
                    className={`border rounded-md p-2.5 text-[13px] ${
                      isLive ? "border-teal-200 bg-teal-50/40"
                      : isScheduled ? "border-teal-100 bg-teal-50/20"
                      : "border-slate-100"
                    }`}
                    data-testid={`admin-announcements-history-row-${h.id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold ${TONE_META[h.tone]?.chipCls || TONE_META.info.chipCls}`}>
                      {h.tone}
                    </span>
                    {isLive && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-teal-100 text-teal-800">Live</span>
                    )}
                    {isScheduled && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-teal-50 text-teal-700 border border-teal-200"
                            data-testid={`admin-announcements-badge-scheduled-${h.id}`}>
                        <Clock className="w-2.5 h-2.5" /> Scheduled
                      </span>
                    )}
                    {h.status === "canceled" && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-slate-100 text-slate-600">Canceled</span>
                    )}
                    {h.status === "superseded" && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-slate-100 text-slate-600">Superseded</span>
                    )}
                    <span className="text-[10px] text-slate-400 font-mono-num ml-auto">
                      {isScheduled
                        ? `Queued for ${h.scheduled_for ? new Date(h.scheduled_for).toLocaleString() : "?"}`
                        : (h.published_at || "").slice(0, 16).replace("T", " · ")}
                    </span>
                  </div>
                  <div className="text-slate-800"
                       dangerouslySetInnerHTML={{ __html: renderRichAnnouncement(h.body) }} />
                  {!isScheduled && (
                    <div className="mt-2 flex items-center gap-3 text-[11px] font-mono-num"
                         data-testid={`admin-announcements-analytics-${h.id}`}>
                      <span className="inline-flex items-center gap-1 text-slate-700">
                        <Users className="w-3 h-3" /> {h.seen_count} seen
                      </span>
                      <span className="text-slate-300">·</span>
                      <span className="inline-flex items-center gap-1 text-slate-700">
                        <EyeOff className="w-3 h-3" /> {h.dismissed_count} dismissed
                      </span>
                      {total > 0 && (
                        <div className="flex-1 max-w-[160px] h-1.5 bg-slate-200 rounded overflow-hidden">
                          <div className="h-full bg-teal-500" style={{ width: `${dRatio}%` }} title={`${dRatio}% read + dismissed`} />
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
